/**
 * Avatar utility functions for managing user avatars
 */

import { User } from 'firebase/auth';

/**
 * Generates a random avatar ID between 1 and 40
 */
export function generateRandomAvatarId(): number {
  return Math.floor(Math.random() * 40) + 1;
}

/**
 * Gets the avatar image path for a given avatar ID
 */
export function getAvatarPath(avatarId: number): string {
  // Ensure avatarId is within valid range
  const validId = Math.max(1, Math.min(40, avatarId));
  return `/avatars/${validId}.png`;
}

/**
 * Gets a random avatar path
 */
function getRandomAvatarPath(): string {
  const avatarId = generateRandomAvatarId();
  return getAvatarPath(avatarId);
}

/**
 * Generates a deterministic avatar ID based on user ID
 * This ensures the same user gets the same avatar across sessions
 */
export function generateDeterministicAvatarId(userId: string): number {
  // Simple hash function to convert userId to a number
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    const char = userId.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  // Convert to positive number and map to 1-40 range
  const positiveHash = Math.abs(hash);
  return (positiveHash % 40) + 1;
}

/**
 * Gets avatar path for a user, using deterministic generation if no avatarId provided
 */
function getUserAvatarPath(userId: string, avatarId?: number): string {
  const id = avatarId || generateDeterministicAvatarId(userId);
  return getAvatarPath(id);
}

/**
 * Checks if a user has a Google profile photo
 */
export function hasGoogleAvatar(user: User | null): boolean {
  return !!(user && user.photoURL && !user.isAnonymous);
}

/**
 * Normalizes Google avatar URL for better compatibility
 * Google profile photos sometimes have size parameters that can be optimized
 */
export function normalizeGoogleAvatarUrl(photoURL: string): string {
  if (!photoURL) return photoURL;

  // If it's a Google profile photo, ensure we get a reasonable size
  if (photoURL.includes('googleusercontent.com')) {
    // Remove existing size parameters and add our own
    const baseUrl = photoURL.split('=')[0];
    return `${baseUrl}=s96-c`; // 96px size, cropped
  }

  return photoURL;
}

/**
 * Gets avatar URL for voting interface with Google support
 * Priority: Google photoURL > vote data avatarId > deterministic avatar
 */
export function getVotingAvatarUrl(
  user: User | null,
  userId: string,
  avatarId?: number,
  photoURL?: string,
  _isAnonymous?: boolean
): string {
  // First priority: Google photo from vote data
  if (photoURL) {
    return normalizeGoogleAvatarUrl(photoURL);
  }

  // Second priority: Google photo from current user (for current user's row)
  if (user && hasGoogleAvatar(user)) {
    return normalizeGoogleAvatarUrl(user.photoURL!);
  }

  // Third priority: Avatar ID from vote data
  if (avatarId && userId) {
    return getUserAvatarPath(userId, avatarId);
  }

  // Fourth priority: Deterministic avatar based on userId
  if (userId) {
    return getUserAvatarPath(userId);
  }

  // Fallback: Default random avatar
  return getRandomAvatarPath();
}
